import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import { LIST_PROCESSOR_POOL_QUERY_KEY } from '@/state/queries/processor-admin/use-list-processor-pool';
import { PLATFORM_SUMMARY_QUERY_KEY } from '@/state/queries/ops-admin/use-platform-summary';
import { ATTENTION_QUEUE_QUERY_KEY } from '@/state/queries/ops-admin/use-attention-queue';

function invalidateProcessorQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [LIST_PROCESSOR_POOL_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [PLATFORM_SUMMARY_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [ATTENTION_QUEUE_QUERY_KEY] });
}

export function useCreateAzureProcessorMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name?: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/processor/players/azure`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: name ? { name } : {},
        token: token ?? '',
      }) as Promise<{ playerId: string; vmId: string }>;
    },
    onSuccess: () => invalidateProcessorQueries(queryClient),
  });
}

export function useCreateStandaloneProcessorMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name?: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/processor/players/standalone`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: name ? { name } : {},
        token: token ?? '',
      }) as Promise<{ playerId: string }>;
    },
    onSuccess: () => invalidateProcessorQueries(queryClient),
  });
}

export function useProcessorMaintenanceMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/processor/players/${encodeURIComponent(playerId)}/maintenance`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: {},
        token: token ?? '',
      }) as Promise<{ playerId: string; opsStatus: string }>;
    },
    onSuccess: () => invalidateProcessorQueries(queryClient),
  });
}

export function useProcessorPauseMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/processor/players/${encodeURIComponent(playerId)}/pause`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: {},
        token: token ?? '',
      }) as Promise<{ playerId: string; opsStatus: string }>;
    },
    onSuccess: () => invalidateProcessorQueries(queryClient),
  });
}

export function useProcessorResumeMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/processor/players/${encodeURIComponent(playerId)}/resume`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: {},
        token: token ?? '',
      }) as Promise<{ playerId: string; opsStatus: string }>;
    },
    onSuccess: () => invalidateProcessorQueries(queryClient),
  });
}

export function useDeleteProcessorMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/processor/players/${encodeURIComponent(playerId)}`;
      return sendJSONRequest({
        url,
        method: METHOD.DELETE,
        body: {},
        token: token ?? '',
      }) as Promise<{ playerId: string }>;
    },
    onSuccess: () => invalidateProcessorQueries(queryClient),
  });
}
